import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { ReactElement, useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Container, Typography } from '@mui/material'
import { useForm } from '@tanstack/react-form'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectUseTexasJurisprudence,
  TEXAS_JURISPRUDENCE_COURSE_IDS,
} from 'src/v3/settings/settings.slice'
import {
  fetchLinkedServices,
  fetchCourses,
  fetchTrainingSeries,
  fetchAssignmentSummariesV4,
  selectLinkedServicesStatus,
  selectActiveLinkedService,
  activeLinkedServiceChanged,
  selectCoursesByLinkedService,
  selectCoursesStatus,
  selectTrainingSeriesByLinkedService,
  selectTrainingSeriesStatus,
  fetchLearningPaths,
  fetchFoamfratLearningPaths,
  selectLearningPathsByLinkedService,
  selectLearningPathsStatus,
  selectFoamfratLearningPathsStatus,
  fetchCompletedAssignmentsWithinThreshold,
  selectCompletedAssignmentsWithinThreshold,
  selectCompletedAssignmentsWithinThresholdStatus,
  fetchLightweightUsersV4,
  selectLightweightUsersV4ByService,
  selectLightweightUsersV4StatusByService,
  fetchGroups,
  selectGroupsWithDefaults,
  selectGroupsStatus,
  createScheduledAssignment,
  fetchScheduledAssignments,
} from 'src/v3/manage/manage.slice'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import WizardNavigation from './components/form-wizard-navigation'
import AssignmentUserSelectionStep from './components/form-user-selection-step'
import AssignmentReviewStep from './components/form-review-step'
import ConfirmationModal from '../components/modals/confirmation-modal'
import SummaryModal from './components/modals/summary-modal'
import ThresholdModal from './components/modals/threshold-modal'
import PageTitleWithLinkedService from '../components/page-title-with-linked-service'
import { confirmThreshholdCompletions, createAssignment } from '../utils/api'
import { WizardStep } from './types'
import { useNotifications } from '../hooks/use-notifications'
import { useLinkedServiceName } from '../hooks/use-linked-service-name'
import { ASSIGNMENTS_URLS } from '../constants'
import UnifiedDetailsCoursesStep from '../shared/unified-details-courses-step'
import type { UnifiedAssignmentFormData } from '../shared/unified-assignment-types'
import {
  calculateDueDaysAfterGoLive,
  normalizeRecurrence,
} from '../utils/date-utils'

dayjs.extend(utc)

export default function AssignmentCreateV4(): ReactElement {
  let navigate = useNavigate()
  let location = useLocation()
  let dispatch = useAppDispatch()

  let linkedServiceID = useAppSelector(selectActiveLinkedService)

  let [step, setStep] = useState<WizardStep>('details-courses')
  let [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  let [isSubmitting, setIsSubmitting] = useState(false)
  let { showError, showSuccess, NotificationComponent } = useNotifications()
  let [summaryModalOpen, setSummaryModalOpen] = useState(false)
  let [summaryModalType, setSummaryModalType] = useState<'users' | 'courses'>(
    'users',
  )
  let [thresholdDialogOpen, setThresholdDialogOpen] = useState(false)
  let [isThresholdSubmitting, setIsThresholdSubmitting] = useState(false)
  let [createdAssignmentId, setCreatedAssignmentId] = useState<string>('')

  let useTexasJurisprudence = useAppSelector(selectUseTexasJurisprudence)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let allCourses = useAppSelector(
    selectCoursesByLinkedService(linkedServiceID || ''),
  )
  let courses = useTexasJurisprudence
    ? allCourses
    : allCourses.filter((c) => !TEXAS_JURISPRUDENCE_COURSE_IDS.includes(c.id))
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let trainingSeries = useAppSelector(
    selectTrainingSeriesByLinkedService(linkedServiceID || ''),
  )
  let trainingSeriesStatus = useAppSelector(selectTrainingSeriesStatus)
  let learningPaths = useAppSelector(
    selectLearningPathsByLinkedService(linkedServiceID || ''),
  )
  let learningPathsStatus = useAppSelector(selectLearningPathsStatus)
  let foamfratLearningPathsStatus = useAppSelector(
    selectFoamfratLearningPathsStatus,
  )
  let completedAssignmentsWithinThreshold = useAppSelector(
    selectCompletedAssignmentsWithinThreshold(createdAssignmentId),
  )
  let completedAssignmentsWithinThresholdStatus = useAppSelector(
    selectCompletedAssignmentsWithinThresholdStatus(createdAssignmentId),
  )

  let linkedServiceName = useLinkedServiceName(linkedServiceID)

  let lightweightUsers = useAppSelector(
    selectLightweightUsersV4ByService(linkedServiceID || ''),
  )
  let lightweightUsersStatus = useAppSelector(
    selectLightweightUsersV4StatusByService(linkedServiceID || ''),
  )

  let groups = useAppSelector(selectGroupsWithDefaults(linkedServiceID || ''))
  let groupsStatus = useAppSelector(selectGroupsStatus)

  let form = useForm({
    defaultValues: {
      mode: 'immediate',
      name: '',
      linkedServiceID: linkedServiceID || '',
      dueDate: '',
      userIDs: [] as string[],
      courseIDs: [] as string[],
      goLiveDate: '',
      hasRecurrence: false,
      recurrence: undefined,
    } as UnifiedAssignmentFormData,
    validators: {
      onChange: ({ value }) => {
        let fieldErrors: Array<[string, string]> = [
          // Validate based on mode
          ...(value.mode === 'immediate'
            ? [
                ...(!value.name
                  ? [
                      ['name', 'Assignment name is required'] as [
                        string,
                        string,
                      ],
                    ]
                  : []),
                ...(!value.dueDate
                  ? [['dueDate', 'Due date is required'] as [string, string]]
                  : []),
                ...(value.dueDate && !dayjs(value.dueDate).isAfter(dayjs())
                  ? [
                      ['dueDate', 'Due date must be in the future'] as [
                        string,
                        string,
                      ],
                    ]
                  : []),
              ]
            : [
                ...(!value.name
                  ? [
                      ['name', 'Assignment name is required'] as [
                        string,
                        string,
                      ],
                    ]
                  : []),
                ...(!value.goLiveDate
                  ? [
                      ['goLiveDate', 'Go live date is required'] as [
                        string,
                        string,
                      ],
                    ]
                  : []),
                ...(!value.dueDate
                  ? [['dueDate', 'Due date is required'] as [string, string]]
                  : []),
              ]),
          ...(!value.courseIDs || value.courseIDs.length === 0
            ? [
                ['courseIDs', 'At least one course must be selected'] as [
                  string,
                  string,
                ],
              ]
            : []),
          ...(!value.userIDs || value.userIDs.length === 0
            ? [
                ['userIDs', 'At least one user must be selected'] as [
                  string,
                  string,
                ],
              ]
            : []),
        ]

        return fieldErrors.length > 0
          ? { fields: Object.fromEntries(fieldErrors) }
          : undefined
      },
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true)

      if (value.mode === 'immediate') {
        let payload = {
          linkedServiceID: value.linkedServiceID,
          name: value.name,
          dueDate: value.dueDate,
          userIDs: value.userIDs,
          courseIDs: value.courseIDs,
        }

        await createAssignment(payload)
          .then((res) => {
            setCreatedAssignmentId(res.id)
            dispatch(fetchAssignmentSummariesV4())
            dispatch(fetchCompletedAssignmentsWithinThreshold(res.id))
          })
          .catch((err: unknown) => {
            let errorMessage =
              err instanceof Error ? err.message : 'Unknown error'
            // eslint-disable-next-line no-console
            console.error(
              'Error creating assignment:',
              'Payload: ',
              payload,
              'Error: ',
              errorMessage,
            )
            showError('Failed to create assignment')
          })
          .finally(() => setIsSubmitting(false))
      } else {
        let dueDaysAfterGoLive = calculateDueDaysAfterGoLive(
          value.goLiveDate!,
          value.dueDate,
        )

        let recurrence =
          value.hasRecurrence && value.recurrence
            ? normalizeRecurrence(value.recurrence)
            : undefined

        let payload = {
          linkedServiceID: value.linkedServiceID,
          name: value.name,
          goLiveDate: value.goLiveDate!,
          dueDaysAfterGoLive,
          userIDs: value.userIDs,
          courseIDs: value.courseIDs,
          recurrence,
        }

        dispatch(createScheduledAssignment(payload))

        if (linkedServiceID) {
          dispatch(fetchScheduledAssignments(linkedServiceID))
        }

        showSuccess('Scheduled assignment created successfully')
        setIsSubmitting(false)
        navigate('/manage/scheduled-assignments')
      }
    },
  })

  let handleThresholdConfirm = async () => {
    setIsThresholdSubmitting(true)

    await confirmThreshholdCompletions(createdAssignmentId)
      .then(() =>
        navigate(`${ASSIGNMENTS_URLS.INDEX}/${createdAssignmentId}`, {
          state: {
            successMessage:
              'Assignment created successfully. Users within threshold have been marked complete.',
          },
        }),
      )
      .catch((err: unknown) => {
        let errorMessage = err instanceof Error ? err.message : 'Unknown error'
        // eslint-disable-next-line no-console
        console.error(
          'Error processing threshold completions:',
          'Assignment ID: ',
          createdAssignmentId,
          'Error: ',
          errorMessage,
        )
        showError('Failed to process threshold completions')
      })
      .finally(() => {
        setIsThresholdSubmitting(false)
        setThresholdDialogOpen(false)
      })
  }

  let handleThresholdDecline = () => {
    setThresholdDialogOpen(false)
    navigate(`${ASSIGNMENTS_URLS.INDEX}/${createdAssignmentId}`, {
      state: {
        successMessage: 'Assignment created successfully.',
      },
    })
  }

  let handleStepChange = (newStep: WizardStep) => {
    setStep(newStep)
  }

  let handleCancel = (formValues?: any) => {
    if (
      formValues &&
      (formValues.name ||
        formValues.userIDs?.length > 0 ||
        formValues.courseIDs?.length > 0)
    ) {
      setCancelDialogOpen(true)
    } else {
      navigate(ASSIGNMENTS_URLS.INDEX, {
        state: { linkedServiceID },
      })
    }
  }

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  // Handle linked service from navigation state (preserved on refresh)
  useEffect(() => {
    let state = location.state as { linkedServiceID?: string } | null

    // If navigating with a linkedServiceID in state, use that
    if (state?.linkedServiceID && state.linkedServiceID !== linkedServiceID) {
      dispatch(activeLinkedServiceChanged(state.linkedServiceID))
      // Clear the state after using it
      navigate(location.pathname, {
        replace: true,
        state: { ...state, linkedServiceID: undefined },
      })
    }
  }, [location.state, location.pathname, linkedServiceID, navigate, dispatch])

  // Redirect if no linked service selected
  useEffect(() => {
    if (
      linkedServicesStatus === 'fulfilled' &&
      (!linkedServiceID || linkedServiceID === 'unselected')
    ) {
      navigate(ASSIGNMENTS_URLS.INDEX)
    }
  }, [linkedServiceID, linkedServicesStatus, navigate])

  useAutoFetch(coursesStatus, fetchCourses)
  useAutoFetch(trainingSeriesStatus, fetchTrainingSeries)
  useAutoFetch(foamfratLearningPathsStatus, fetchFoamfratLearningPaths)

  useEffect(() => {
    if (
      learningPathsStatus === 'uninitialized' &&
      linkedServiceID &&
      linkedServiceID !== 'unselected'
    ) {
      dispatch(fetchLearningPaths(linkedServiceID))
    }
  }, [learningPathsStatus, linkedServiceID, dispatch])

  useEffect(() => {
    if (
      linkedServiceID &&
      lightweightUsersStatus !== 'fulfilled' &&
      lightweightUsersStatus !== 'pending'
    ) {
      dispatch(fetchLightweightUsersV4(linkedServiceID))
    }
  }, [linkedServiceID, lightweightUsersStatus, dispatch])

  useEffect(() => {
    if (groupsStatus !== 'fulfilled' && groupsStatus !== 'pending') {
      dispatch(fetchGroups())
    }
  }, [groupsStatus, dispatch])

  // Threshold detection effect
  useEffect(() => {
    if (
      createdAssignmentId &&
      completedAssignmentsWithinThresholdStatus === 'fulfilled'
    ) {
      if (
        completedAssignmentsWithinThreshold &&
        completedAssignmentsWithinThreshold.length > 0
      ) {
        // Show threshold dialog
        setThresholdDialogOpen(true)
      } else {
        // No threshold users, navigate directly
        navigate(`${ASSIGNMENTS_URLS.INDEX}/${createdAssignmentId}`, {
          state: {
            successMessage: 'Assignment created successfully.',
          },
        })
      }
    }
  }, [
    createdAssignmentId,
    completedAssignmentsWithinThresholdStatus,
    completedAssignmentsWithinThreshold,
    navigate,
  ])

  return (
    <Container maxWidth="xl" className="py-6">
      <PageTitleWithLinkedService
        pageTitle="Create Assignment"
        moduleName="Assignments"
        linkedServiceName={linkedServiceName || ''}
        handleBackClick={handleCancel}
      />
      <WizardNavigation currentStep={step} onStepChange={handleStepChange} />
      {step === 'details-courses' && (
        <form.Subscribe
          selector={(state) => ({
            values: state.values,
            fieldMeta: state.fieldMeta,
          })}
        >
          {({ values, fieldMeta }) => {
            let canContinue =
              values.mode === 'immediate'
                ? Boolean(
                    values.name &&
                    fieldMeta.name?.isValid &&
                    !fieldMeta.name?.errors?.length &&
                    values.dueDate &&
                    fieldMeta.dueDate?.isValid &&
                    !fieldMeta.dueDate?.errors?.length &&
                    values.courseIDs?.length > 0 &&
                    fieldMeta.courseIDs?.isValid &&
                    !fieldMeta.courseIDs?.errors?.length,
                  )
                : Boolean(
                    values.name &&
                    fieldMeta.name?.isValid &&
                    !fieldMeta.name?.errors?.length &&
                    values.goLiveDate &&
                    fieldMeta.goLiveDate?.isValid &&
                    !fieldMeta.goLiveDate?.errors?.length &&
                    values.dueDate &&
                    fieldMeta.dueDate?.isValid &&
                    !fieldMeta.dueDate?.errors?.length &&
                    values.courseIDs?.length > 0 &&
                    fieldMeta.courseIDs?.isValid &&
                    !fieldMeta.courseIDs?.errors?.length,
                  )

            return (
              <UnifiedDetailsCoursesStep
                form={form}
                courses={courses}
                trainingSeries={trainingSeries}
                learningPaths={learningPaths}
                onCancel={() => handleCancel(values)}
                onContinue={() => handleStepChange('users')}
                canContinue={canContinue}
                isLoadingCourses={coursesStatus === 'pending'}
                linkedServiceID={linkedServiceID || undefined}
                showModeSelector
              />
            )
          }}
        </form.Subscribe>
      )}
      {step === 'users' && (
        <form.Field name="userIDs" mode="array">
          {(field) => (
            <form.Subscribe
              selector={(state) => ({
                values: state.values,
                fieldMeta: state.fieldMeta,
              })}
            >
              {({ values, fieldMeta }) => {
                let canContinue = Boolean(
                  values.userIDs?.length > 0 &&
                  fieldMeta.userIDs?.isValid &&
                  !fieldMeta.userIDs?.errors?.length,
                )

                return (
                  <AssignmentUserSelectionStep
                    values={{ userIDs: values.userIDs }}
                    errors={{
                      userIDs: field.state.meta.errors?.[0],
                    }}
                    users={lightweightUsers}
                    groups={groups as any}
                    onValueChange={(_, value) => field.handleChange(value)}
                    onCancel={() => handleCancel(values)}
                    onBack={() => setStep('details-courses')}
                    onContinue={() => handleStepChange('review')}
                    canContinue={canContinue}
                    isLoadingUsers={false}
                  />
                )
              }}
            </form.Subscribe>
          )}
        </form.Field>
      )}
      {step === 'review' && (
        <form.Subscribe selector={(state) => state.values}>
          {(formValues) => (
            <AssignmentReviewStep
              formData={formValues}
              onCancel={() => handleCancel(formValues)}
              onBack={() => setStep('users')}
              onSubmit={() => form.handleSubmit()}
              onShowUsersSummary={() => {
                setSummaryModalType('users')
                setSummaryModalOpen(true)
              }}
              onShowCoursesSummary={() => {
                setSummaryModalType('courses')
                setSummaryModalOpen(true)
              }}
              isSubmitting={isSubmitting}
            />
          )}
        </form.Subscribe>
      )}
      <ConfirmationModal
        isOpen={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={() => navigate(ASSIGNMENTS_URLS.INDEX)}
        title="Cancel Assignment Creation?"
        confirmText="Yes, Cancel"
        cancelText="Continue Editing"
        confirmColor="error"
      >
        <Typography>
          Are you sure you want to cancel? Any changes will be lost.
        </Typography>
      </ConfirmationModal>
      <form.Subscribe
        selector={(state) => [state.values.userIDs, state.values.courseIDs]}
      >
        {([userIDs, courseIDs]) => (
          <SummaryModal
            isOpen={summaryModalOpen}
            onClose={() => setSummaryModalOpen(false)}
            type={summaryModalType}
            users={lightweightUsers}
            courses={courses}
            selectedUserIDs={userIDs}
            selectedCourseIDs={courseIDs}
          />
        )}
      </form.Subscribe>
      <ThresholdModal
        open={thresholdDialogOpen}
        onClose={handleThresholdDecline}
        onConfirm={handleThresholdConfirm}
        completedAssignmentsWithinThreshold={
          completedAssignmentsWithinThreshold || []
        }
        users={lightweightUsers}
        courses={courses}
        isSubmitting={isThresholdSubmitting}
      />
      {NotificationComponent}
    </Container>
  )
}
